/**
 * Alert Analysis State — Svelte 5 runes
 *
 * Stores quick insights from the unified AlertAnalysisService.
 * Map<alert_flag_id, QuickInsight> for O(1) lookup by alert row.
 *
 * Populated by:
 *  - getBatchInsights() REST call on alerts page mount
 *  - ALERT_ANALYSIS_UPDATE WebSocket messages (real-time)
 */

// ============================================================================
// Types
// ============================================================================

export type Severity = 'low' | 'medium' | 'high' | 'critical';

export interface QuickInsight {
	alert_flag_id: string;
	one_liner: string;
	severity: Severity;
	cost_per_day: number | null;
	approval_rate: number;
	ui_action: string;
	decision: string;
	confidence: number;
	should_approve: boolean;
}

/**
 * Deep analysis report shape — produced by analyze_alert_deep on the
 * backend. Mirrors the structure declared in
 * `llm_prompts.AUTO_TRIAGE_DEEP_SYSTEM_PROMPT`. Every field is optional
 * at this layer because the LLM response is not strict-schema
 * validated; the UI renders what's present and falls back for what
 * isn't.
 */
export interface DeepAnalysisReport {
	triage_decision?: {
		approval_rate?: number;
		ui_action?: string;
		should_approve?: boolean;
		decision?: string;
		priority?: string;
		one_liner?: string;
		ui_comment?: string;
		confidence?: number;
	};
	root_cause_hypotheses?: Array<{
		hypothesis: string;
		confidence: number;
		evidence: string[];
	}>;
	historical_pattern?: string;
	remediation_plan?: Array<{
		step: number;
		action: string;
		expected_outcome?: string;
		effort?: 'low' | 'med' | 'high';
	}>;
	fleet_comparison?: string;
	citations?: Array<{
		type: string;
		id?: string;
		note?: string;
	}>;
	confidence_summary?: {
		data_completeness?: number;
		reasoning_confidence?: number;
		notes?: string;
	};
	rich_report?: DeepRichReport;
}

export type DeepRichTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface DeepRichMetric {
	label: string;
	value: string;
	tone?: DeepRichTone;
}

export interface DeepRichTimelineEvent {
	time?: string;
	label: string;
	detail?: string;
	tone?: DeepRichTone;
}

export interface DeepRichHypothesis {
	hypothesis: string;
	confidence?: number;
	evidence?: string[];
}

export interface DeepRichStep {
	label: string;
	detail?: string;
	expected_outcome?: string;
	tone?: DeepRichTone;
}

export interface DeepRichChartSpec {
	kind: 'bar' | 'line';
	labels: string[];
	series: Array<{
		label: string;
		values: number[];
		tone?: DeepRichTone;
	}>;
}

export type DeepRichBlock =
	| { type: 'markdown'; title?: string; content: string }
	| { type: 'metric_grid'; title?: string; metrics: DeepRichMetric[] }
	| { type: 'evidence_table'; title?: string; columns: string[]; rows: string[][] }
	| { type: 'timeline'; title?: string; events: DeepRichTimelineEvent[] }
	| { type: 'hypothesis_list'; title?: string; hypotheses: DeepRichHypothesis[] }
	| { type: 'steps'; title?: string; steps: DeepRichStep[] }
	| { type: 'warnings'; title?: string; items: string[] }
	| { type: 'chart'; title?: string; chart: DeepRichChartSpec };

export interface DeepRichReport {
	format_version: '1' | string;
	title?: string;
	summary_markdown?: string;
	blocks?: DeepRichBlock[];
	citations?: Array<{
		label: string;
		source?: string;
		detail?: string;
	}>;
}

export interface FullAnalysis {
	id: string;
	alert_flag_id: string;
	organization_id: string;
	created_at: string | null;
	// Quick insight fields
	one_liner: string | null;
	severity: Severity | null;
	cost_per_day: number | null;
	// Triage decision
	approval_rate: number | null;
	should_approve: boolean | null;
	ui_action: string | null;
	ui_comment: string | null;
	decision: string;
	confidence: number | null;
	priority: string | null;
	// Full analysis
	full_response: Record<string, unknown> | null;
	reasoning: Record<string, unknown> | null;
	enrichment_data: Record<string, unknown> | null;
	// Deep analysis (P1 — on-demand)
	deep_analysis: DeepAnalysisReport | null;
	deep_generated_at: string | null;
	deep_model_used: string | null;
	// Alert identity — promoted from stored JSONB
	asset_name?: string | null;
	field_name?: string | null;
	site_id?: string | null;
	manager?: string | null;
	alert_type?: string | null;
	component_id?: string | null;
	component_name?: string | null;
	start_time?: string | null;
	end_time?: string | null;
	// Audit trail
	status: string;
	reviewed_by: string | null;
	reviewed_at: string | null;
	review_action: string | null;
	work_order_id: string | null;
	model_used: string | null;
	prompt_version: string | null;
}

// ============================================================================
// State
// ============================================================================

/** Map of alert_flag_id → QuickInsight for instant row lookups */
let insights = $state<Map<string, QuickInsight>>(new Map());

/** Whether a batch fetch is currently in flight */
let isLoading = $state(false);

/** Last error from batch fetch */
let lastError = $state<string | null>(null);

// ============================================================================
// Accessors
// ============================================================================

export const alertAnalysesState = {
	get insights() {
		return insights;
	},
	get isLoading() {
		return isLoading;
	},
	get lastError() {
		return lastError;
	}
};

// ============================================================================
// Mutations
// ============================================================================

/** Replace all insights (e.g. from batch API response) */
export function setInsights(batch: QuickInsight[]): void {
	const next = new Map(insights);
	for (const qi of batch) {
		next.set(qi.alert_flag_id, qi);
	}
	insights = next;
}

/** Upsert a single insight (e.g. from WebSocket push) */
export function upsertInsight(qi: QuickInsight): void {
	const next = new Map(insights);
	next.set(qi.alert_flag_id, qi);
	insights = next;
}

/** Clear all stored insights */
export function clearInsights(): void {
	insights = new Map();
}

/** Mark loading state */
export function setInsightsLoading(loading: boolean): void {
	isLoading = loading;
}

/** Store error */
export function setInsightsError(error: string | null): void {
	lastError = error;
}

/** Get a single insight by alert_flag_id (O(1)) */
export function getInsight(alertFlagId: string): QuickInsight | undefined {
	return insights.get(alertFlagId);
}
